<template>
  <div class="container-fluid">
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing shadow-sm">
      <!-- En-tête -->
      <div class="card-header d-flex justify-content-between align-items-center py-4 px-4"
        style="background-color: #0a59a4;">
        <div>
          <h3 class="text-white fw-bold mb-1">Import de Manifeste Navire</h3>
          <small class="text-white opacity-75">Téléversez un fichier XML/EDI de manifeste armateur</small>
        </div>
      </div>

      <div class="card-body p-15 p-sm-20 p-md-25">
        
        <div v-if="!uploadedFile && !importing" class="upload-zone mb-4">
          <div 
            class="dropzone-area border-2 border-dashed rounded p-5 text-center"
            :class="{ 'border-primary bg-light': isDragging, 'border-secondary': !isDragging }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
            style="cursor: pointer; min-height: 250px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
            
            <i class="flaticon-upload fs-1 text-primary mb-3"></i>
            <h5 class="fw-semibold mb-2">Glissez votre fichier ici</h5>
            <p class="text-muted mb-3">ou cliquez pour parcourir</p>
            
            <div class="text-muted small">
              <p class="mb-1">Formats acceptés : <strong>.xml, .edi</strong></p>
              <p class="mb-0">Taille maximale : <strong>10 MB</strong></p>
            </div>

            <input 
              ref="fileInput" 
              type="file" 
              accept=".xml,.edi" 
              @change="handleFileSelect" 
              class="d-none" 
            />
          </div>
        </div>

        <!-- Informations du fichier sélectionné -->
        <div v-if="uploadedFile && !importing" class="file-info-section mb-4">
          <div class="alert alert-info d-flex align-items-center">
            <i class="flaticon-file-1 fs-3 me-3"></i>
            <div class="flex-grow-1">
              <h6 class="mb-1 fw-semibold">{{ uploadedFile.name }}</h6>
              <small class="text-muted">
                Taille : {{ formatFileSize(uploadedFile.size) }} | 
                Type : {{ uploadedFile.type || 'application/xml' }}
              </small>
            </div>
            <button 
              class="btn btn-sm btn-outline-danger" 
              @click="cancelUpload"
              type="button">
              <i class="flaticon-delete"></i> Annuler
            </button>
          </div>

          <div class="card border mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 fw-semibold">Options d'import</h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                
                <!-- Système source -->
                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-2">Système source (optionnel)</label>
                  <input 
                    type="text" 
                    v-model="importOptions.sourceSystem" 
                    class="form-control shadow-sm" 
                    placeholder="ex: ANTASER, Code armateur..."
                  />
                </div>

                <!-- Compagnie maritime -->
                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-2">Compagnie maritime (optionnel)</label>
                  <input 
                    type="text" 
                    v-model="importOptions.carrierCode" 
                    class="form-control shadow-sm" 
                    placeholder="ex: APLFW, AMSC..."
                  />
                </div>

                <!-- Options automatiques -->
                <div class="col-12">
                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      v-model="importOptions.autoCreateVoyage" 
                      id="autoCreateVoyage"
                    />
                    <label class="form-check-label" for="autoCreateVoyage">
                      Créer automatiquement le voyage si inexistant
                    </label>
                  </div>

                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      v-model="importOptions.autoCreatePortCalls" 
                      id="autoCreatePortCalls"
                    />
                    <label class="form-check-label" for="autoCreatePortCalls">
                      Créer automatiquement les escales
                    </label>
                  </div>

                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      v-model="importOptions.autoMatchBls" 
                      id="autoMatchBls"
                    />
                    <label class="form-check-label" for="autoMatchBls">
                      Tenter le rapprochement automatique avec les BL BESC
                    </label>
                  </div>

                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      v-model="importOptions.blockDuplicates" 
                      id="blockDuplicates"
                    />
                    <label class="form-check-label" for="blockDuplicates">
                      Bloquer si fichier déjà importé (via checksum)
                    </label>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- Bouton d'import -->
          <div class="d-flex justify-content-end gap-3">
            <button 
              class="btn btn-secondary px-4" 
              @click="cancelUpload"
              type="button">
              Annuler
            </button>
            <button 
              class="btn btn-primary px-5 py-2 text-white" 
              @click="startImport"
              type="button"
              :disabled="loading">
              <i class="flaticon-upload me-2"></i>
              {{ loading ? 'Import en cours...' : 'Importer le manifeste' }}
            </button>
          </div>
        </div>

        <!-- Barre de progression -->
        <div v-if="importing" class="import-progress-section mb-4">
          <div class="card border">
            <div class="card-body">
              <h6 class="fw-semibold mb-3">Import en cours...</h6>
              
              <div class="progress mb-3" style="height: 25px;">
                <div 
                  class="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  :style="{ width: importProgress + '%' }"
                  :aria-valuenow="importProgress" 
                  aria-valuemin="0" 
                  aria-valuemax="100">
                  {{ importProgress }}%
                </div>
              </div>

              <!-- Étapes d'import -->
              <ul class="list-unstyled mb-0">
                <li class="mb-2" :class="getStepClass('parsing')">
                  <i :class="getStepIcon('parsing')"></i>
                  Parsing XML
                </li>
                <li class="mb-2" :class="getStepClass('validation')">
                  <i :class="getStepIcon('validation')"></i>
                  Validation structure
                </li>
                <li class="mb-2" :class="getStepClass('voyage')">
                  <i :class="getStepIcon('voyage')"></i>
                  Création voyage
                </li>
                <li class="mb-2" :class="getStepClass('bls')">
                  <i :class="getStepIcon('bls')"></i>
                  Import BL
                </li>
                <li class="mb-2" :class="getStepClass('containers')">
                  <i :class="getStepIcon('containers')"></i>
                  Import conteneurs
                </li>
                <li :class="getStepClass('cargo')">
                  <i :class="getStepIcon('cargo')"></i>
                  Import marchandises
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Résultat succès -->
        <div v-if="importResult && importResult.success" class="import-result-success">
          <div class="alert alert-success">
            <div class="d-flex align-items-start">
              <i class="flaticon-check-mark fs-2 me-3"></i>
              <div class="flex-grow-1">
                <h5 class="fw-bold mb-3"> Import réussi !</h5>
                
                <div class="card border mb-3">
                  <div class="card-body">
                    <h6 class="fw-semibold mb-3">Statistiques</h6>
                    <div class="row g-3">
                      <div class="col-md-6">
                        <p class="mb-2">
                          <strong>ID Manifeste :</strong> 
                          <code>{{ importResult.data.manifestId }}</code>
                        </p>
                        <p class="mb-2">
                          <strong>Voyage créé :</strong> 
                          {{ importResult.data.voyageCode }} 
                          (Navire: {{ importResult.data.shipName }})
                        </p>
                        <p class="mb-2">
                          <strong>BL importés :</strong> {{ importResult.data.blsCount }}
                        </p>
                        <p class="mb-2">
                          <strong>Conteneurs :</strong> {{ importResult.data.containersCount }}
                        </p>
                      </div>
                      <div class="col-md-6">
                        <p class="mb-2">
                          <strong>Lignes de marchandises :</strong> {{ importResult.data.cargoItemsCount }}
                        </p>
                        <p class="mb-2 text-success">
                          <strong>BL rapprochés automatiquement :</strong> {{ importResult.data.matchedBlsCount }}
                        </p>
                        <p class="mb-2 text-warning">
                          <strong>BL non rapprochés :</strong> {{ importResult.data.unmatchedBlsCount }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Avertissement pour BL non rapprochés -->
                <div v-if="importResult.data.unmatchedBlsCount > 0" class="alert alert-warning mb-3">
                  <i class="flaticon-warning me-2"></i>
                  <strong class="text text-black">Attention :</strong> {{ importResult.data.unmatchedBlsCount }} BL n'ont pas pu être rapprochés automatiquement.
                  <br>
                  <small class="text text-black">Vous devrez effectuer un rapprochement manuel.</small>
                  <div class="mt-2">
                    <button 
                      class="btn btn-sm btn-warning" 
                      @click="goToManualMatching(importResult.data.manifestId)"
                      type="button">
                      <i class="flaticon-link me-2"></i>
                      Rapprocher manuellement
                    </button>
                  </div>
                </div>

                <div class="d-flex gap-3">
                  <button 
                    class="btn btn-primary" 
                    @click="viewManifestDetails(importResult.data.manifestId)"
                    type="button">
                    <i class="flaticon-eye me-2"></i>
                    Voir les détails du manifeste
                  </button>
                  <button 
                    class="btn btn-success" 
                    @click="resetUpload"
                    type="button">
                    <i class="flaticon-plus me-2"></i>
                    Importer un autre manifeste
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Résultat erreur -->
        <div v-if="importResult && !importResult.success" class="import-result-error">
          <div class="alert alert-danger">
            <div class="d-flex align-items-start">
              <i class="flaticon-close fs-2 me-3"></i>
              <div class="flex-grow-1">
                <h5 class="fw-bold mb-3">Erreur d'import</h5>
                
                <div class="card border border-danger mb-3">
                  <div class="card-body">
                    <p class="mb-2"><strong>Code erreur :</strong> {{ importResult.error.code }}</p>
                    <p class="mb-0"><strong>Message :</strong> {{ importResult.error.message }}</p>
                  </div>
                </div>

                <div class="d-flex gap-3">
                  <button 
                    class="btn btn-primary" 
                    @click="resetUpload"
                    type="button">
                    <i class="flaticon-refresh me-2"></i>
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { useRouter } from 'vue-router'
import ApiService from '@/services/ApiService'
import { success, error } from '@/utils/utils'

export default defineComponent({
  name: 'UploadManifest',
  
  setup() {
    const router = useRouter()
    const uploadedFile = ref<File | null>(null)
    const isDragging = ref(false)
    const loading = ref(false)
    const importing = ref(false)
    const importProgress = ref(0)
    const currentStep = ref('')
    const importResult = ref<any>(null)
    const fileInput = ref<HTMLInputElement | null>(null)

    const importOptions = ref({
      sourceSystem: '',
      carrierCode: '',
      autoCreateVoyage: true,
      autoCreatePortCalls: true,
      autoMatchBls: true,
      blockDuplicates: true
    })

    const completedSteps = ref<string[]>([])

    const triggerFileInput = () => {
      fileInput.value?.click()
    }

    const handleFileSelect = (event: Event) => {
      const target = event.target as HTMLInputElement
      if (target.files && target.files.length > 0) {
        validateAndSetFile(target.files[0])
      }
    }

    const handleDrop = (event: DragEvent) => {
      isDragging.value = false
      if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
        validateAndSetFile(event.dataTransfer.files[0])
      }
    }

    const validateAndSetFile = (file: File) => {
      const maxSize = 10 * 1024 * 1024
      const allowedTypes = ['.xml', '.edi']
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()

      if (file.size > maxSize) {
        error('Le fichier est trop volumineux. Taille maximale : 10 MB')
        return
      }

      if (!allowedTypes.includes(fileExtension)) {
        error('Format de fichier non accepté. Formats acceptés : .xml, .edi')
        return
      }

      uploadedFile.value = file
      success('Fichier sélectionné avec succès')
    }

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }

    const cancelUpload = () => {
      uploadedFile.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const viewManifestDetails = (manifestId: string) => {
      router.push(`/view-manifest/${manifestId}`)
    }

    const goToManualMatching = (manifestId: string) => {
      router.push(`/view-manifest/${manifestId}?tab=matching`)
    }

    const getErrorMessage = (errorCode: string, defaultMessage: string): string => {
      const errorMessages: Record<string, string> = {
        'DUPLICATE_FILE': 'Ce fichier a déjà été importé. Désactivez "Bloquer les doublons" pour réimporter.',
        'PARSE_ERROR': 'Erreur de parsing du fichier. Vérifiez que le format XML/EDI est correct.',
        'VOYAGE_NOT_FOUND': 'Voyage introuvable. Activez "Créer automatiquement le voyage".',
        'INVALID_FILE_FORMAT': 'Format de fichier invalide. Utilisez uniquement .xml ou .edi',
        'FILE_TOO_LARGE': 'Fichier trop volumineux (max 10 MB).'
      }
      return errorMessages[errorCode] || defaultMessage
    }

    const startImport = async () => {
      if (!uploadedFile.value) return

      loading.value = true
      importing.value = true
      importProgress.value = 0
      completedSteps.value = []
      currentStep.value = 'parsing'

      try {
        const formData = new FormData()
        formData.append('file', uploadedFile.value)
        formData.append('sourceSystem', importOptions.value.sourceSystem)
        formData.append('carrierCode', importOptions.value.carrierCode)
        formData.append('autoCreateVoyage', String(importOptions.value.autoCreateVoyage))
        formData.append('autoCreatePortCalls', String(importOptions.value.autoCreatePortCalls))
        formData.append('autoMatchBls', String(importOptions.value.autoMatchBls))
        formData.append('blockDuplicates', String(importOptions.value.blockDuplicates))

        simulateProgress()

        const response = await ApiService.postWithFile('/manifests/upload', formData)

        importProgress.value = 100
        completedSteps.value = ['parsing', 'validation', 'voyage', 'bls', 'containers', 'cargo']

        if (response.data.code === 200) {
          importResult.value = {
            success: true,
            data: response.data.data
          }
          success('Manifeste importé avec succès !')
        } else {
          throw new Error(response.data.message || 'Erreur inconnue')
        }

      } catch (err: any) {
        console.error('Erreur import:', err)
        
        const errorCode = err.response?.data?.code || 'IMPORT_ERROR'
        const errorMessage = getErrorMessage(
          errorCode,
          err.response?.data?.message || err.message || 'Erreur lors de l\'import du manifeste'
        )
        
        importResult.value = {
          success: false,
          error: {
            code: errorCode,
            message: errorMessage
          }
        }
        error(errorMessage)
      } finally {
        loading.value = false
        importing.value = false
      }
    }

    const simulateProgress = () => {
      const steps = ['parsing', 'validation', 'voyage', 'bls', 'containers', 'cargo']
      let stepIndex = 0

      const interval = setInterval(() => {
        if (stepIndex < steps.length) {
          currentStep.value = steps[stepIndex]
          completedSteps.value.push(steps[stepIndex])
          importProgress.value = Math.min(((stepIndex + 1) / steps.length) * 90, 90)
          stepIndex++
        } else {
          clearInterval(interval)
        }
      }, 800)
    }

    const getStepClass = (step: string) => {
      if (completedSteps.value.includes(step)) {
        return 'text-success fw-semibold'
      } else if (currentStep.value === step) {
        return 'text-primary fw-semibold'
      }
      return 'text-muted'
    }

    const getStepIcon = (step: string) => {
      if (completedSteps.value.includes(step)) {
        return 'flaticon-check-mark text-success me-2'
      } else if (currentStep.value === step) {
        return 'fa fa-spinner fa-spin text-primary me-2'
      }
      return 'flaticon-circle text-muted me-2'
    }

    const resetUpload = () => {
      uploadedFile.value = null
      importResult.value = null
      importProgress.value = 0
      completedSteps.value = []
      currentStep.value = ''
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    return {
      uploadedFile,
      isDragging,
      loading,
      importing,
      importProgress,
      currentStep,
      importResult,
      fileInput,
      importOptions,
      completedSteps,
      triggerFileInput,
      handleFileSelect,
      handleDrop,
      formatFileSize,
      cancelUpload,
      startImport,
      getStepClass,
      getStepIcon,
      resetUpload,
      viewManifestDetails,
      goToManualMatching
    }
  }
})
</script>

<style scoped>
.dropzone-area {
  transition: all 0.3s ease;
}

.dropzone-area:hover {
  border-color: #0a59a4 !important;
  background-color: #f8f9fa;
}
</style>
